"use client"

import { motion, useReducedMotion } from "framer-motion"
import type { ReactNode } from "react"
import LandingCta from "./LandingCta"
import LandingMockApp from "./LandingMockApp"

const EASE = [0.32, 0.72, 0, 1] as const

const PHRASE_DURATION = 0.9
const PHRASE_STAGGER = -0.75
const AFTER_HEADLINE = -0.2
const BLOCK_DURATION = 0.9
const BLOCK_STAGGER = -0.65
const MOCK_DURATION = 1.2
const MOCK_STAGGER = -0.7
const MOCK_WASH_DURATION = MOCK_DURATION * 2
const MOCK_WASH_AT = 0.5

function delayAfter(start: number, duration: number, gap: number) {
  return Math.max(0, start + duration + gap)
}

const HEADLINE = [
  { id: "top", phrase: "One account. No borders.", breakAfter: true },
  { id: "bottom", phrase: "Your financial life, onchain." },
] as const

const lastPhraseDelay = delayAfter(0, PHRASE_DURATION, PHRASE_STAGGER)
const subheadDelay = delayAfter(
  lastPhraseDelay,
  PHRASE_DURATION,
  AFTER_HEADLINE
)
const ctaDelay = delayAfter(subheadDelay, BLOCK_DURATION, BLOCK_STAGGER)
const mockDelay = delayAfter(ctaDelay, BLOCK_DURATION, MOCK_STAGGER)
const mockSettle = mockDelay + MOCK_DURATION
const mockWashDelay = mockDelay + MOCK_DURATION * MOCK_WASH_AT

const rest = { opacity: 1, y: 0, filter: "blur(0px)" }
const headlineEnter = { opacity: 0, y: 10, filter: "blur(5px)" }
const blockEnter = { opacity: 0, y: -10, filter: "blur(5px)" }
const mockEnter = { opacity: 0, y: -10, filter: "blur(5px)" }

export default function LandingHero() {
  const reduceMotion = useReducedMotion()

  return (
    <section className="relative flex flex-col">
      <div className="pointer-events-none absolute inset-0 z-1 mx-auto w-full max-w-7xl border-black/11 border-x max-xl:border-none" />

      <div className="mx-auto flex w-full max-w-7xl flex-col">
        <div className="flex flex-col items-start gap-6 px-6 pt-16 sm:px-8 sm:pt-24 sm:pb-16">
          <h1 className="flex-1 text-balance font-medium text-4xl/10 text-gray-900 tracking-tight lg:text-6xl/15">
            {HEADLINE.map((item, index) => (
              <span key={item.id}>
                <motion.span
                  className="inline-block"
                  initial={reduceMotion ? false : headlineEnter}
                  animate={rest}
                  transition={{
                    duration: PHRASE_DURATION,
                    ease: EASE,
                    delay: reduceMotion ? 0 : index === 0 ? 0 : lastPhraseDelay,
                  }}
                >
                  {item.phrase}
                </motion.span>{" "}
                {"breakAfter" in item && <br className="hidden md:block" />}
              </span>
            ))}
          </h1>

          <FadeBlock
            delay={subheadDelay}
            duration={BLOCK_DURATION}
            enter={blockEnter}
            rest={rest}
            reduceMotion={reduceMotion}
          >
            <p className="text-balance font-medium text-gray-500 text-lg/7">
              Crypto, stocks, bonds, precious metals, yield, and more—
              <br className="hidden md:block" />
              brought together in one borderless account, confidential by
              design.
            </p>
          </FadeBlock>

          <FadeBlock
            delay={ctaDelay}
            duration={BLOCK_DURATION}
            enter={blockEnter}
            rest={rest}
            reduceMotion={reduceMotion}
          >
            <div className="flex flex-wrap items-center gap-5">
              <LandingCta
                location="hero"
                size="xl"
                className="h-12!"
                data-testid="landing-hero-cta"
              >
                Create account
              </LandingCta>
              <p className="whitespace-nowrap font-medium text-base/6 text-gray-500">
                No email. No KYC. Ready in seconds.
              </p>
            </div>
          </FadeBlock>
        </div>
      </div>

      <div className="relative overflow-hidden">
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0)_10%,#CCFFE1_28%,#99FFC4_46%,#65FBAA_63%,#31F49C_81%,#00EC97_100%)]"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: MOCK_WASH_DURATION,
            ease: EASE,
            delay: reduceMotion ? 0 : mockWashDelay,
          }}
        />
        <FadeBlock
          delay={mockDelay}
          duration={MOCK_DURATION}
          enter={mockEnter}
          rest={rest}
          reduceMotion={reduceMotion}
        >
          <LandingMockApp demoStartDelay={mockSettle} />
        </FadeBlock>
      </div>
    </section>
  )
}

function FadeBlock({
  delay,
  duration,
  enter,
  rest,
  reduceMotion,
  children,
}: {
  delay: number
  duration: number
  enter: { opacity: number; y: number; filter: string }
  rest: { opacity: number; y: number; filter: string }
  reduceMotion: boolean | null
  children: ReactNode
}) {
  return (
    <motion.div
      initial={reduceMotion ? false : enter}
      animate={rest}
      transition={{
        duration,
        ease: EASE,
        delay: reduceMotion ? 0 : delay,
      }}
    >
      {children}
    </motion.div>
  )
}
